// @ts-ignore - vendored self-contained Three.js module.
import * as THREE from '../../vendor/three.module.min.js';
import type { LevelConfig } from '../game/types.js';
import { MaterialLibrary } from './MaterialLibrary.js';

const MAX_DUST = 56;
const MAX_TUFTS = 110;
const MAX_ACCENTS = 30;

/** 各景致配色表（实例色）。 */
const TUFT_COLORS: Record<string, number[]> = {
  meadow: [0x58b368, 0x6fd97a, 0x4da35c, 0x8ee07a],
  desert: [0xc9a86a, 0xd9bc7f, 0xb9985f],
  moon: [0x6f6aa8, 0x7f79c2, 0x5d589a],
};
const ACCENT_COLORS: Record<string, number[]> = {
  meadow: [0xff8fc7, 0xffe066, 0xffffff, 0xff7a7a, 0xc07bff],
  desert: [0x4e9a51, 0x5fb661, 0x458a48],
  moon: [0x54f0ed, 0xb96dcb, 0xff7fc4],
};
const DUST_COLORS: Record<string, number[]> = {
  meadow: [0xfff6d8, 0xffffff, 0xbfffe9],
  desert: [0xffe0b0, 0xffd18f, 0xffffff],
  moon: [0x9fe8ff, 0xc07bff, 0xffffff],
};

/**
 * 场景氛围造型器（自包含；在 TankGame 的灯光/背景之上叠加装饰层）。
 *
 * - 天空穹顶：倒置大球 + 按关卡调色板重绘的竖直渐变画布
 *   （草原/荒漠带卡通太阳，月堡撒星星+月球），fog 关闭保持通透；
 * - 飘浮尘埃/花瓣/星尘：加性小泡按正弦场浮动，颜色随景致；
 * - 地面装饰：场外环带种子化摆放草丛（锥）+ 景致点缀
 *   （草原花朵带花茎 / 荒漠仙人掌 / 月堡霓虹水晶），不进游戏区；
 * - stylizeLights：软化阴影（radius/normalBias）并按 arena 实际
 *   尺寸贴合阴影视锥，避免大地图边缘阴影裁切。
 *
 * 装饰位置由 (level.id, scenery) 种子化，PvP 两端看到的布局一致。
 * 全部装饰走 5 块稳定网格（天空/尘埃/草丛/花茎/点缀），
 * update 热路径零分配；尘埃/草丛每帧重写矩阵，开销可忽略。
 * （2026-07-26：移除漂移云——云上飘会遮挡俯视视野，影响对战体验。）
 */
export class SceneStyler {
  private readonly root = new THREE.Group();
  private readonly dummy = new THREE.Object3D();
  private readonly tempColor = new THREE.Color();
  private readonly tempColorB = new THREE.Color();

  private readonly skyCanvas: HTMLCanvasElement;
  private readonly skyTexture: any;
  private readonly skyMaterial: any;
  private readonly skyMesh: any;
  private readonly skyGeometry: any;

  private readonly dustInstances: any;
  private readonly dustMaterial: any;
  private readonly dustGeometry: any;

  private readonly tuftInstances: any;
  private readonly tuftMaterial: any;
  private readonly tuftGeometry: any;

  private readonly stemInstances: any;
  private readonly stemMaterial: any;
  private readonly stemGeometry: any;

  private accentInstances: any = null;
  private accentMaterial: any = null;
  private accentGeometry: any = null;

  // 装饰参数（预分配到上限，激活数量随画质分档变化）。
  private readonly dustX = new Float32Array(MAX_DUST);
  private readonly dustY = new Float32Array(MAX_DUST);
  private readonly dustZ = new Float32Array(MAX_DUST);
  private readonly dustPhase = new Float32Array(MAX_DUST);
  private readonly dustScale = new Float32Array(MAX_DUST);
  private readonly tuftX = new Float32Array(MAX_TUFTS);
  private readonly tuftZ = new Float32Array(MAX_TUFTS);
  private readonly tuftScale = new Float32Array(MAX_TUFTS);
  private readonly tuftRot = new Float32Array(MAX_TUFTS);
  private readonly tuftPhase = new Float32Array(MAX_TUFTS);
  private readonly accentX = new Float32Array(MAX_ACCENTS);
  private readonly accentZ = new Float32Array(MAX_ACCENTS);
  private readonly accentScale = new Float32Array(MAX_ACCENTS);
  private readonly accentRot = new Float32Array(MAX_ACCENTS);

  private elapsed = 0;
  private qualityScale: number;
  private activeDust = MAX_DUST;
  private activeTufts = MAX_TUFTS;
  private activeAccents = MAX_ACCENTS;
  private tuftSway = true;
  private disposed = false;
  /** apply(level) 至少跑过一次后才允许 update/静态矩阵写入，避免未配置时原点堆装饰。 */
  private configured = false;

  constructor(private readonly scene: any, private readonly materials: MaterialLibrary, qualityScale = 1) {
    this.qualityScale = THREE.MathUtils.clamp(qualityScale, 0.35, 1);
    this.root.name = 'scene-styler';

    // 天空穹顶：竖直渐变画布贴在倒置球内面，renderOrder -1 + 不写深度，
    // 永远垫底；fog 关闭使其不被场景雾吞掉。
    this.skyCanvas = document.createElement('canvas');
    this.skyCanvas.width = 64;
    this.skyCanvas.height = 512;
    this.skyTexture = new THREE.CanvasTexture(this.skyCanvas);
    this.skyTexture.colorSpace = THREE.SRGBColorSpace;
    this.skyTexture.wrapS = THREE.RepeatWrapping;
    this.skyTexture.wrapT = THREE.ClampToEdgeWrapping;
    this.skyGeometry = new THREE.SphereGeometry(110, 24, 12);
    this.skyMaterial = new THREE.MeshBasicMaterial({ map: this.skyTexture, side: THREE.BackSide, fog: false, depthWrite: false });
    this.skyMesh = new THREE.Mesh(this.skyGeometry, this.skyMaterial);
    this.skyMesh.renderOrder = -1;
    this.skyMesh.frustumCulled = false;
    this.root.add(this.skyMesh);

    this.dustGeometry = new THREE.SphereGeometry(0.09, 6, 4);
    this.dustMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    this.dustInstances = this.createSurface(this.dustGeometry, this.dustMaterial, MAX_DUST);
    this.root.add(this.dustInstances);

    this.tuftGeometry = new THREE.ConeGeometry(0.15, 0.62, 5);
    this.tuftMaterial = this.materials.toon(0xffffff, {});
    this.tuftMaterial.vertexColors = true;
    this.tuftInstances = this.createSurface(this.tuftGeometry, this.tuftMaterial, MAX_TUFTS);
    this.root.add(this.tuftInstances);

    this.stemGeometry = new THREE.CylinderGeometry(0.035, 0.045, 0.5, 5);
    this.stemMaterial = this.materials.toon(0x3f8f4f, {});
    this.stemInstances = this.createSurface(this.stemGeometry, this.stemMaterial, MAX_ACCENTS);
    this.root.add(this.stemInstances);

    this.scene.add(this.root);
    this.applyQualityCounts();
  }

  private createSurface(geometry: any, material: any, capacity: number): any {
    const mesh = new THREE.InstancedMesh(geometry, material, capacity);
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // 一个零缩放实例预编译着色器，避免进关卡首帧卡顿。
    mesh.setMatrixAt(0, new THREE.Matrix4().makeScale(0, 0, 0));
    mesh.setColorAt(0, this.tempColor.setHex(0xffffff));
    if (mesh.instanceColor) mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    mesh.count = 1;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    return mesh;
  }

  /** 按关卡调色板/景致重配氛围（buildLevel/setupEnvironment 后调用）。 */
  apply(level: LevelConfig): void {
    const scenery = level.scenery;
    const seed = level.id * 7919 + (scenery === 'meadow' ? 11 : scenery === 'desert' ? 22 : 33);
    const random = this.seededRandom(seed);
    const halfW = level.arena.width / 2;
    const halfD = level.arena.depth / 2;

    this.paintSky(level, random);

    // 尘埃/花瓣/星尘：场内低空漂浮。
    const dustColors = DUST_COLORS[scenery] ?? DUST_COLORS.meadow;
    for (let i = 0; i < MAX_DUST; i += 1) {
      this.dustX[i] = (random() * 2 - 1) * halfW;
      this.dustZ[i] = (random() * 2 - 1) * halfD;
      this.dustY[i] = 0.4 + random() * 2.2;
      this.dustPhase[i] = random() * Math.PI * 2;
      this.dustScale[i] = 0.55 + random() * 0.75;
      this.dustInstances.setColorAt(i, this.tempColor.setHex(dustColors[i % dustColors.length]));
    }
    if (this.dustInstances.instanceColor) this.dustInstances.instanceColor.needsUpdate = true;

    // 草丛与点缀：只落在场外环带（含边际安全距离），游戏区保持干净。
    const tuftColors = TUFT_COLORS[scenery] ?? TUFT_COLORS.meadow;
    for (let i = 0; i < MAX_TUFTS; i += 1) {
      this.scatterOutside(random, halfW, halfD, this.tuftX, this.tuftZ, i);
      this.tuftScale[i] = 0.75 + random() * 0.75;
      this.tuftRot[i] = random() * Math.PI * 2;
      this.tuftPhase[i] = random() * Math.PI * 2;
      this.tuftInstances.setColorAt(i, this.tempColor.setHex(tuftColors[i % tuftColors.length]));
    }
    if (this.tuftInstances.instanceColor) this.tuftInstances.instanceColor.needsUpdate = true;

    const accentColors = ACCENT_COLORS[scenery] ?? ACCENT_COLORS.meadow;
    for (let i = 0; i < MAX_ACCENTS; i += 1) {
      this.scatterOutside(random, halfW, halfD, this.accentX, this.accentZ, i);
      this.accentScale[i] = 0.8 + random() * 0.7;
      this.accentRot[i] = random() * Math.PI * 2;
    }

    this.rebuildAccent(scenery, accentColors);
    this.stemInstances.visible = scenery === 'meadow';
    this.configured = true;
    this.applyQualityCounts();
    this.syncStaticDecor();
  }

  /**
   * 灯光参数化打磨：软化阴影、按 arena 实际尺寸贴合阴影视锥
   * （PvP 大竞技场比 PvE 地图宽，固定 ±24 会在边缘裁掉阴影）。
   * 入参为 TankGame 已有的灯光实例；缺项跳过。
   */
  stylizeLights(
    lights: { hemisphere?: any; keyLight?: any; fillLight?: any; rimLight?: any },
    level: LevelConfig,
  ): void {
    const moon = level.scenery === 'moon';
    const desert = level.scenery === 'desert';
    if (lights.hemisphere) lights.hemisphere.intensity = moon ? 2.3 : 2.15;
    const key = lights.keyLight;
    if (key) {
      key.intensity = moon ? 2.7 : desert ? 3.5 : 3.3;
      if (key.shadow) {
        // PCFSoft 上半径软化 + normalBias 防大地图阴影痤疮。
        key.shadow.radius = 4;
        key.shadow.normalBias = 0.025;
        key.shadow.bias = -0.0006;
        const camera = key.shadow.camera;
        if (camera) {
          const hw = level.arena.width / 2 + 3;
          const hd = level.arena.depth / 2 + 3;
          camera.left = -hw;
          camera.right = hw;
          camera.top = hd;
          camera.bottom = -hd;
          camera.updateProjectionMatrix?.();
        }
      }
    }
    if (lights.fillLight) lights.fillLight.intensity = moon ? 1.35 : 1.15;
    if (lights.rimLight) lights.rimLight.intensity = desert ? 0.95 : 0.85;
  }

  setQualityScale(value: number): void {
    const wasSway = this.tuftSway;
    this.qualityScale = THREE.MathUtils.clamp(value, 0.35, 1);
    this.applyQualityCounts();
    // 高档才开草摆；关停时把草丛矩阵复位到静止姿态。
    if (wasSway && !this.tuftSway) this.syncStaticDecor();
  }

  update(delta: number): void {
    if (this.disposed || !this.configured) return;
    this.elapsed += delta;
    this.syncDust();
    if (this.tuftSway) this.syncTufts(this.elapsed);
  }

  /** 释放全部自建资源（quitToMenu/热重载等整树拆除路径调用）。 */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scene.remove(this.root);
    for (const mesh of [this.skyMesh, this.dustInstances, this.tuftInstances, this.stemInstances, this.accentInstances]) {
      mesh?.dispose?.();
    }
    this.skyGeometry.dispose();
    this.skyTexture.dispose();
    this.skyMaterial.dispose();
    this.dustGeometry.dispose();
    this.dustMaterial.dispose();
    this.tuftGeometry.dispose();
    this.tuftMaterial.dispose();
    this.stemGeometry.dispose();
    this.stemMaterial.dispose();
    this.disposeAccent();
    this.root.clear();
  }

  get diagnostics(): Record<string, number> {
    return {
      dust: this.activeDust,
      tufts: this.activeTufts,
      accents: this.activeAccents,
      drawSurfaces: 5,
    };
  }

  /** 竖直渐变 + 太阳/月球/星星（按景致）；画布 64×512，build 期重绘。 */
  private paintSky(level: LevelConfig, random: () => number): void {
    const context = this.skyCanvas.getContext('2d')!;
    const w = this.skyCanvas.width;
    const h = this.skyCanvas.height;
    const sky = this.tempColor.setHex(level.palette.sky).clone();
    const fog = this.tempColorB.setHex(level.palette.fog).clone();
    const moon = level.scenery === 'moon';
    const zenith = sky.clone().multiplyScalar(moon ? 0.5 : 0.62);
    const horizon = sky.clone().lerp(moon ? fog : new THREE.Color(0xffffff), moon ? 0.45 : 0.5);

    const gradient = context.createLinearGradient(0, 0, 0, h);
    gradient.addColorStop(0, `#${zenith.getHexString()}`);
    gradient.addColorStop(0.55, `#${sky.getHexString()}`);
    gradient.addColorStop(0.86, `#${horizon.getHexString()}`);
    gradient.addColorStop(1, `#${fog.getHexString()}`);
    context.fillStyle = gradient;
    context.fillRect(0, 0, w, h);

    if (moon) {
      // 星点（种子化，两端一致）+ 一轮淡月。
      for (let i = 0; i < 110; i += 1) {
        context.globalAlpha = 0.35 + random() * 0.6;
        context.fillStyle = random() > 0.7 ? '#bfe8ff' : '#ffffff';
        context.beginPath();
        context.arc(random() * w, random() * h * 0.62, 0.5 + random() * 1.2, 0, Math.PI * 2);
        context.fill();
      }
      context.globalAlpha = 0.9;
      const moonGlow = context.createRadialGradient(w * 0.68, h * 0.17, 2, w * 0.68, h * 0.17, 26);
      moonGlow.addColorStop(0, '#f4f1ff');
      moonGlow.addColorStop(0.45, '#cfc8f2');
      moonGlow.addColorStop(1, 'rgba(120,110,190,0)');
      context.fillStyle = moonGlow;
      context.beginPath();
      context.arc(w * 0.68, h * 0.17, 26, 0, Math.PI * 2);
      context.fill();
    } else {
      // 卡通太阳：暖白核心 + 柔和光晕。
      const sun = context.createRadialGradient(w * 0.32, h * 0.16, 2, w * 0.32, h * 0.16, 30);
      sun.addColorStop(0, '#fffbe8');
      sun.addColorStop(0.4, '#ffefb0');
      sun.addColorStop(1, 'rgba(255,220,130,0)');
      context.globalAlpha = 0.95;
      context.fillStyle = sun;
      context.beginPath();
      context.arc(w * 0.32, h * 0.16, 30, 0, Math.PI * 2);
      context.fill();
    }
    context.globalAlpha = 1;
    this.skyTexture.needsUpdate = true;
  }

  /** 景致点缀几何/材质随 scenery 重建（build 期，旧件即弃即毁）。 */
  private rebuildAccent(scenery: string, colors: number[]): void {
    this.disposeAccent();
    if (scenery === 'desert') {
      this.accentGeometry = new THREE.BoxGeometry(0.3, 0.9, 0.3);
      this.accentMaterial = this.materials.toon(0xffffff, {});
    } else if (scenery === 'moon') {
      // 霓虹水晶：无光照亮色贴合月堡霓虹美学。
      this.accentGeometry = new THREE.TetrahedronGeometry(0.34, 0);
      this.accentMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true, toneMapped: false });
    } else {
      this.accentGeometry = new THREE.SphereGeometry(0.17, 8, 6);
      this.accentMaterial = this.materials.toon(0xffffff, {});
    }
    this.accentMaterial.vertexColors = true;
    this.accentInstances = this.createSurface(this.accentGeometry, this.accentMaterial, MAX_ACCENTS);
    for (let i = 0; i < MAX_ACCENTS; i += 1) {
      this.accentInstances.setColorAt(i, this.tempColor.setHex(colors[i % colors.length]));
    }
    if (this.accentInstances.instanceColor) this.accentInstances.instanceColor.needsUpdate = true;
    this.root.add(this.accentInstances);
  }

  private disposeAccent(): void {
    if (!this.accentInstances) return;
    this.root.remove(this.accentInstances);
    this.accentInstances.dispose?.();
    this.accentGeometry?.dispose?.();
    this.accentMaterial?.dispose?.();
    this.accentInstances = null;
    this.accentGeometry = null;
    this.accentMaterial = null;
  }

  /** 在场外环带取一点（|x|<halfW+0.9 且 |z|<halfD+0.9 的场内区拒绝）。 */
  private scatterOutside(random: () => number, halfW: number, halfD: number, outX: Float32Array, outZ: Float32Array, index: number): void {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const x = (random() * 2 - 1) * (halfW + 13.2);
      const z = (random() * 2 - 1) * (halfD + 13.2);
      if (Math.abs(x) < halfW + 0.9 && Math.abs(z) < halfD + 0.9) continue;
      outX[index] = x;
      outZ[index] = z;
      return;
    }
    // 兜底：贴左边带（种子化序列下几乎不会走到）。
    outX[index] = -(halfW + 1.4 + random() * 11);
    outZ[index] = (random() * 2 - 1) * (halfD + 13.2);
  }

  private applyQualityCounts(): void {
    const q = this.qualityScale;
    this.activeDust = Math.max(0, Math.round(MAX_DUST * q));
    this.activeTufts = Math.max(28, Math.round(MAX_TUFTS * q));
    this.activeAccents = Math.max(8, Math.round(MAX_ACCENTS * q));
    // 草摆只开高档，省低端的每帧矩阵重写。
    this.tuftSway = q >= 0.9;
  }

  /** 静止装饰（草丛/点缀/花茎）矩阵一次性写入；草摆关闭时也用它复位。 */
  private syncStaticDecor(): void {
    if (!this.configured) return;
    this.syncTufts(-1);
    if (this.accentInstances) {
      for (let i = 0; i < this.activeAccents; i += 1) {
        const s = this.accentScale[i];
        // 花朵球心抬高 / 仙人掌立起 / 水晶半埋，统一用 y 偏移表达。
        this.dummy.position.set(this.accentX[i], 0.42 * s + 0.08, this.accentZ[i]);
        this.dummy.rotation.set(0, this.accentRot[i], 0);
        this.dummy.scale.setScalar(s);
        this.dummy.updateMatrix();
        this.accentInstances.setMatrixAt(i, this.dummy.matrix);
      }
      this.commitInstances(this.accentInstances, this.activeAccents);
    }
    if (this.stemInstances.visible) {
      for (let i = 0; i < this.activeAccents; i += 1) {
        this.dummy.position.set(this.accentX[i], 0.25, this.accentZ[i]);
        this.dummy.rotation.set(0, 0, 0);
        this.dummy.scale.setScalar(1);
        this.dummy.updateMatrix();
        this.stemInstances.setMatrixAt(i, this.dummy.matrix);
      }
    }
    this.commitInstances(this.stemInstances, this.stemInstances.visible ? this.activeAccents : 0);
  }

  private syncTufts(elapsed: number): void {
    const sway = elapsed >= 0;
    for (let i = 0; i < this.activeTufts; i += 1) {
      const s = this.tuftScale[i];
      this.dummy.position.set(this.tuftX[i], 0.31 * s, this.tuftZ[i]);
      this.dummy.rotation.set(0, this.tuftRot[i], sway ? Math.sin(elapsed * 1.35 + this.tuftPhase[i]) * 0.08 : 0);
      this.dummy.scale.setScalar(s);
      this.dummy.updateMatrix();
      this.tuftInstances.setMatrixAt(i, this.dummy.matrix);
    }
    this.commitInstances(this.tuftInstances, this.activeTufts);
  }

  private syncDust(): void {
    for (let i = 0; i < this.activeDust; i += 1) {
      const phase = this.dustPhase[i];
      this.dummy.position.set(
        this.dustX[i] + Math.sin(this.elapsed * 0.22 + phase) * 0.9,
        this.dustY[i] + Math.sin(this.elapsed * 0.6 + phase * 1.7) * 0.4,
        this.dustZ[i] + Math.cos(this.elapsed * 0.19 + phase) * 0.9,
      );
      this.dummy.rotation.set(0, 0, 0);
      // 呼吸式明灭：缩放到 0 即隐形（加性混合下等效淡出）。
      this.dummy.scale.setScalar(Math.max(0.001, this.dustScale[i] * (0.7 + 0.3 * Math.sin(this.elapsed * 1.1 + phase * 2.3))));
      this.dummy.updateMatrix();
      this.dustInstances.setMatrixAt(i, this.dummy.matrix);
    }
    this.commitInstances(this.dustInstances, this.activeDust);
  }

  private commitInstances(mesh: any, count: number): void {
    if (!mesh) return;
    const countChanged = mesh.count !== count;
    mesh.count = count;
    if (count > 0 || countChanged) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }

  private seededRandom(seed: number): () => number {
    let value = seed >>> 0;
    return () => {
      value = (value * 1664525 + 1013904223) >>> 0;
      return value / 4294967296;
    };
  }
}
